import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechApiErrorCodes } from "@agtech/core/data/http/AgTechApiErrorCodes";
import { useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { invalidateInvoiceQueries, InvoiceDataGetUnprocessedSalesQuery, InvoiceQueryKey } from "../../invoicing/InvoicingDataQueries";
import { useCurrentSale } from "../../sales/SaleDataStore";
import { SaleContactDataGetContactDetailsQuery } from "../../operation/contacts/SaleContactDataQueries";
import { LotDataGetLotsQuery } from "../LotDataQueries";
import { InvoiceDataGetInvoiceLotsQuery } from "./LotInvoiceDataQueries";
import { useQueryClient } from "@tanstack/react-query";

/// DTOs

export type ChangeItemOnInvoiceDTO = {
    remove: boolean,
    id: number
}

// Carries the invoice's last-known concurrency token alongside the change list, so the server can
// detect whether the invoice changed since the client last loaded it (see ChangeItemsOnInvoiceRequestDTO
// on the backend).
export type ChangeItemsOnInvoiceSubmission = {
    changes: ChangeItemOnInvoiceDTO[],
    invoiceTimestamp?: string
}

// Shared by the three actions below - a concurrency conflict means someone else changed this
// invoice since the client last loaded it, mirroring how Lot actions handle ConcurrencyError.
const showInvoiceConcurrencyConflict = (props: { showError: (header: string, details?: string) => void }) => {
    props.showError(
        'This invoice was already updated by someone else',
        'Refreshing the invoice with the latest details. Please review and try again.'
    );
};

/// Actions

export const useLotDataWriteLotInvoiceChangesAction = (inputData: {
    invoiceId: number,
    contactId: number
}): AgTechDataAction<ChangeItemsOnInvoiceSubmission, boolean> => {
    const currentSale = useCurrentSale();
    const queryClient = useQueryClient();

    return {
        name: 'WriteLotChanges',
        getConfiguration: props => ({
            actionExecutionMessage: 'Updating lots...',
            actionConfirmationMessage: 'Successfully updated lots'
        }),
        validate: async props => {
            // TODO: Add validations to prevent removal of all lots on an invoice
        },
        action: async props => {
            return await props.http.post<ChangeItemsOnInvoiceSubmission, boolean>({
                request: `Invoice/${inputData.invoiceId}/ChangeLots`,
                inputData: props.submittedEntity,
            });
        },
        onFailure: async props => {
            if (props.error.errorCode === AgTechApiErrorCodes.ConcurrencyError) {
                showInvoiceConcurrencyConflict(props);

                queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            }
            else {
                props.showError(props.error.message ?? 'An error occurred. Please try again.');
            }
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: inputData.invoiceId
            });

            props.invalidateQuery({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetInvoiceLotsQuery,
                queryContext: {
                    saleId: currentSale.id,
                    invoiceId: inputData.invoiceId,
                    contactId: inputData.contactId
                }
            });
        }
    }
}

export const useInvoiceModifierDataWriteInvoiceModifierInvoiceChangesAction = (inputData: {
    invoiceId: number,
    contactId: number
}): AgTechDataAction<ChangeItemsOnInvoiceSubmission, boolean> => {
    const currentSale = useCurrentSale();
    const queryClient = useQueryClient();

    return {
        name: 'WriteInvoiceModifierChanges',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating charge or discount...',
            actionConfirmationMessage: 'Successfully updated charge or discount'
        }),
        action: async props => {
            return await props.http.post<ChangeItemsOnInvoiceSubmission, boolean>({
                request: `Invoice/${inputData.invoiceId}/ChangeInvoiceModifiers`,
                inputData: props.submittedEntity,
            });
        },
        onFailure: async props => {
            if (props.error.errorCode === AgTechApiErrorCodes.ConcurrencyError) {
                showInvoiceConcurrencyConflict(props);

                queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            }
            else {
                props.showError(props.error.message ?? 'An error occurred. Please try again.');
            }
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: inputData.invoiceId
            });

            props.invalidateQuery({
                query: SaleContactDataGetContactDetailsQuery,
                queryContext: { contactId: inputData.contactId }
            });
        }
    }
}

export const useCreditsDataWriteCreditInvoiceChangesAction = (inputData: {
    invoiceId: number,
    contactId: number
}): AgTechDataAction<ChangeItemsOnInvoiceSubmission, boolean> => {
    const currentSale = useCurrentSale();
    const queryClient = useQueryClient();

    return {
        name: 'WriteCreditChanges',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating credits...',
            actionConfirmationMessage: 'Successfully updated credits'
        }),
        action: async props => {
            return await props.http.post<ChangeItemsOnInvoiceSubmission, boolean>({
                request: `Invoice/${inputData.invoiceId}/ChangeCredits`,
                inputData: props.submittedEntity,
            });
        },
        onFailure: async props => {
            if (props.error.errorCode === AgTechApiErrorCodes.ConcurrencyError) {
                showInvoiceConcurrencyConflict(props);

                queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            }
            else {
                props.showError(props.error.message ?? 'An error occurred. Please try again.');
            }
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: inputData.invoiceId
            });

            props.invalidateQuery({
                query: SaleContactDataGetContactDetailsQuery,
                queryContext: { contactId: inputData.contactId }
            });
        }
    }
}
